import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Building2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SupervisorCard from "@/components/SupervisorCard";
import { Button } from "@/components/ui/button";
import { scoreColor } from "@/lib/traits";

export default function Favorites() {
  const { user, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (user === false) {
      setAuthOpen(true);
      return;
    }
    if (user) {
      api.get("/favorites").then((r) => setData(r.data));
      api.get("/companies").then((r) => setCompanies(r.data));
    }
  }, [user]); // eslint-disable-line

  if (!user) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400">Sign in to view your saved favorites.</div>;
  if (!data) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  const favCompanies = companies.filter((c) => (user.favorite_companies || []).includes(c.name));

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">Your list</p>
      <h1 className="font-display text-5xl text-slate-900 mb-2 flex items-center gap-3" style={{ fontWeight: 900 }}>
        <Heart className="text-rose-500" fill="#f43f5e" /> Saved
      </h1>
      <p className="text-slate-500 mb-10">Supervisors and companies you're keeping an eye on.</p>

      <h2 className="font-display text-2xl text-slate-900 mb-4" style={{ fontWeight: 700 }}>Supervisors</h2>
      {data.supervisors.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400 mb-10" data-testid="favorites-empty-supervisors">
          No saved supervisors yet. Tap the heart on any profile to save it.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="favorite-supervisors-grid">
          {data.supervisors.map((s) => <SupervisorCard key={s.id} sup={s} />)}
        </div>
      )}

      <h2 className="font-display text-2xl text-slate-900 mb-4" style={{ fontWeight: 700 }}>Companies</h2>
      {favCompanies.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400" data-testid="favorites-empty-companies">
          No saved companies yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {favCompanies.map((c) => (
            <button key={c.name} onClick={() => navigate(`/company/${encodeURIComponent(c.name)}`)} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:-translate-y-1 hover:shadow-xl transition-all">
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 rounded-lg bg-slate-900 flex items-center justify-center"><Building2 size={20} className="text-white" /></div>
                <span className="font-display text-2xl" style={{ fontWeight: 800, color: scoreColor(c.org_score) }}>{c.org_score.toFixed(1)}</span>
              </div>
              <div className="font-display text-xl text-slate-900 mt-3" style={{ fontWeight: 700 }}>{c.name}</div>
              <div className="text-sm text-slate-500 mt-1">{c.supervisor_count} leaders · {c.review_count} reviews</div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-12">
        <Button onClick={() => navigate("/supervisors")} variant="outline" className="gap-2 rounded-full">
          Browse supervisors <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}
